import { useParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import {
  getGetApiAgentsAgentIdQueryKey,
  useGetApiAgentsAgentId,
  usePatchApiAgentsAgentId,
} from "@/lib/api/generated/agents/agents";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/shared/status-badge";
import { useTenantContext } from "@/lib/hooks/use-tenant-context";
import type { ControlPlaneAgentRecord } from "@/lib/types/control-plane-agents";
import { AgentTabSurface } from "./agent-tab-surface";

export function AgentOverviewTab() {
  const { agentId } = useParams<{ agentId: string }>();
  const { tenantId } = useTenantContext();
  const queryClient = useQueryClient();
  const { data: agent } = useGetApiAgentsAgentId<ControlPlaneAgentRecord>(agentId!, {
    query: { enabled: Boolean(tenantId && agentId) },
  });
  const saveMutation = usePatchApiAgentsAgentId();

  if (!agent) {return null;}

  const isActive = agent.status === "active";

  const toggleStatus = () => {
    saveMutation.mutate({
      agentId: agent.agentId,
      data: {
        tenantId: tenantId!,
        slug: agent.slug,
        status: isActive ? "paused" : "active",
      },
    }, {
      onSuccess: (updated) => {
        queryClient.setQueryData(
          getGetApiAgentsAgentIdQueryKey(agent.agentId),
          updated as ControlPlaneAgentRecord,
        );
      },
    });
  };

  return (
    <AgentTabSurface
      title="Overview"
      description="Core agent metadata and lifecycle state."
      actions={
        <Button
          size="sm"
          variant={isActive ? "outline" : "default"}
          onClick={toggleStatus}
          disabled={saveMutation.isPending}
        >
          {saveMutation.isPending ? "Saving..." : isActive ? "Pause" : "Activate"}
        </Button>
      }
    >
      <div className="grid gap-x-8 gap-y-4 text-sm sm:grid-cols-2">
        <Detail label="ID" value={agent.agentId} mono />
        <div>
          <span className="text-xs" style={{ color: "var(--muted-foreground)" }}>
            Status
          </span>
          <div className="mt-1">
            <StatusBadge status={agent.status ?? "draft"} />
          </div>
        </div>
        <Detail label="Description" value={agent.description ?? "Not set"} />
        <Detail label="Execution Class" value={agent.runtimePolicy.executionClass} />
      </div>
    </AgentTabSurface>
  );
}

function Detail({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="min-w-0">
      <span className="text-xs" style={{ color: "var(--muted-foreground)" }}>
        {label}
      </span>
      <p className={mono ? "mt-1 break-all font-mono text-xs" : "mt-1"} style={{ color: "var(--text-strong)" }}>
        {value}
      </p>
    </div>
  );
}
